<!-- src/components/SubNav.vue -->
<script setup>
import { computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useModelStore } from "@/composables/useModelStore.js";

const props = defineProps({
  state: { type: String, required: true } // CP | PIM | BMD | BASE
});

const router = useRouter();
const route = useRoute();

// PIM model store (only meaningful when state === "PIM")
const { models, currentModel, setModel } = useModelStore();

/* ============================================================================
   NAV DEFINITIONS PER MAIN SYSTEM
============================================================================ */
const navItems = computed(() => {
  switch (props.state) {

    case "CP":
      return [
        { key: "cp-sync", label: "Synchronisation", route: "/cp/sync" },      
        { key: "cp-import", label: "Import", route: "/cp/import" },
        { key: "cp-config", label: "Config", route: "/cp/config" },
        //{ key: "cp-export", label: "Export", route: "/cp/export" }
      ];
      
    case "SAP":
      return [
        { key: "sap-sync", label: "Synchronisation", route: "/sap/sync" },      
        { key: "sap-export", label: "Export", route: "/sap/export" },
        { key: "sap-config", label: "Config", route: "/sap/config" }        
      ];      

    case "BMD":
      return [
        { key: "bmd-config", label: "Config", route: "/bmd/config" },
        { key: "bmd-import", label: "Import", route: "/bmd/import" },
        { key: "bmd-export", label: "Export", route: "/bmd/export" }
      ];

    case "BASE":
      return [
        { key: "base-config", label: "Config", route: "/base/config" },
        { key: "base-import", label: "Import", route: "/base/import" },
        { key: "base-export", label: "Export", route: "/base/export" }
      ];

    case "PIM":
      return Object.values(models).map(m => ({
        key: m.table,
        label: m.label,
        modelPath: m.path,   // kebab-case path
        modelTable: m.table  // internal identifier
      }));

    default:
      return [];
  }
});

/* ============================================================================
   CLICK HANDLER
============================================================================ */
function onClick(item) {
  // ----------------------------------------------------------
  // PIM → grid model switch (no view navigation)
  // ----------------------------------------------------------
  if (props.state === "PIM" && item.modelPath) {
    setModel(item.modelPath);
    return;
  }

  // ----------------------------------------------------------
  // CP / SAP / BMD / BASE → internal view navigation
  // ----------------------------------------------------------
  if (item.route) {
    router.push(item.route);
  }
}

/* ============================================================================
   ACTIVE STATE
============================================================================ */
function isActive(item) {
  // PIM → active model highlight
  if (props.state === "PIM") {
    return item.modelTable === currentModel.value;
  }

  // Other systems → active by route
  if (item.route) {
    return route.path === item.route;
  }

  return false;
}
</script>

<template>
  <nav class="subnav" aria-label="Secondary Navigation">
    <ul>
      <li
        v-for="item in navItems"
        :key="item.key"
        :class="{ active: isActive(item) }"
        @click="onClick(item)"
      >
        {{ item.label }}
      </li>
    </ul>
  </nav>
</template>

<style lang="less" scoped>
.subnav {
  background: @nodezone-dark;
  padding: 0.6rem 1rem;
  border-bottom: darken(@nodezone-dark, 10%);
}

.subnav ul {
  display: flex;
  gap: 1.25rem;
  list-style: none;
  padding: 0;
  margin: 0;
}

.subnav li {
  position: relative;
  cursor: pointer;
  padding: 0.4rem 0.8rem;
  color: #ccc;
  user-select: none;
  transition: color 0.15s ease, border-bottom 0.15s ease;
}

.subnav{
  li:first-child{
    margin-left: 14px;
  }
}

.subnav li:hover {
  color: @accent-weak;
}

.subnav li.active {
  color: #fff;
  &:after{
    position: absolute;
    z-index: 2;
    left: -10px;
    bottom: -10px;
    font-family: 'icomoon' !important;
    speak: none;
    font-style: normal;
    font-weight: normal;
    font-variant: normal;
    text-transform: none;
    font-size: 20px;
    color: #fff;
    display: inline;
    /* Better Font Rendering =========== */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    content: "\e9ff";

  }
}
</style>
